import { endGatewaySession } from "@api/auth-requests";
import { usePermissionStore, validateToken } from "@auth/auth";
import { USER_TOKEN_KEY_LOCAL_STORAGE } from "@constants/constants";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Loader } from "@ui/loader/loader";
import { AUTH_EVENTS, authEmitter } from "@utils/auth-events";
import { partnerIdInstance } from "@utils/partner-id-instance";
import { tokenInstance } from "@utils/token-instance";
import type { FunctionComponent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AuthContext } from "./auth-context";
import { pendingHandoff } from "./handoff-session";

export const AuthProvider: FunctionComponent<{ children: ReactNode }> = ({
	children,
}) => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(true);
	const resetPermissions = usePermissionStore(
		(state) => state.resetPermissions,
	);
	// A user sent here by another zypl app is signed in by a code still being
	// exchanged. Until that settles nothing below may ask who they are, nor
	// sign them out for having no token yet. Unsettled on every first render,
	// the prerendered shell's included, so hydration always matches; a load
	// without a code settles right after.
	const [handoffSettled, setHandoffSettled] = useState(false);

	const checkAuth = () => {
		const token = tokenInstance.getToken();

		if (token) {
			setIsAuthenticated(true);
		} else {
			logout();
		}
		setIsLoading(false);
	};

	const login = useCallback(() => {
		const token = tokenInstance.getToken();

		if (token) {
			setIsAuthenticated(true);
		}
	}, []);

	const logout = useCallback(() => {
		// Sessions are now kept by the gateway, so clearing this browser alone
		// would leave a usable one behind. Not awaited: an unreachable gateway
		// must not keep the user signed in here.
		void endGatewaySession();

		tokenInstance.clearToken();
		partnerIdInstance.clearPartnerId();
		sessionStorage.clear();
		queryClient.clear();
		setIsAuthenticated(false);
		resetPermissions();

		navigate({ to: "/login" });
	}, [navigate, queryClient, resetPermissions]);

	useEffect(() => {
		const handleUnauthorized = () => {
			logout();
		};

		authEmitter.on(AUTH_EVENTS.UNAUTHORIZED, handleUnauthorized);

		return () => {
			authEmitter.off(AUTH_EVENTS.UNAUTHORIZED, handleUnauthorized);
		};
	}, [logout]);

	// The session is shared through localStorage, so another tab (or another
	// zypl app on this origin) can end it or replace it with someone else's.
	// This tab would otherwise keep its partner, permissions and identity
	// while its requests went out as that other user.
	useEffect(() => {
		const handleStorage = async (event: StorageEvent) => {
			// `key` is null when the whole storage was cleared.
			if (event.key !== null && event.key !== USER_TOKEN_KEY_LOCAL_STORAGE) {
				return;
			}

			if (!tokenInstance.getToken()) {
				if (isAuthenticated) logout();
				return;
			}

			if (!isAuthenticated) {
				login();
				return;
			}

			// A refresh in another tab rotates the token without changing who
			// it belongs to; only a different user calls for a fresh start.
			const previous = queryClient.getQueryData<{ id: string }>([
				"validateToken",
			]);
			if (!previous) return;

			try {
				const current = await validateToken();
				if (current.id !== previous.id) window.location.reload();
			} catch {
				// A dead token surfaces as a 401 on the next request anyway.
			}
		};

		window.addEventListener("storage", handleStorage);

		return () => {
			window.removeEventListener("storage", handleStorage);
		};
	}, [isAuthenticated, login, logout, queryClient]);

	useEffect(() => {
		const handoff = pendingHandoff();
		if (!handoff) {
			setHandoffSettled(true);
			return;
		}

		let active = true;
		void handoff.then(() => {
			if (active) setHandoffSettled(true);
		});

		return () => {
			active = false;
		};
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-validates the stored token whenever the auth state flips; checkAuth is stable in effect (only reads the token and calls setters/logout).
	useEffect(() => {
		if (handoffSettled) checkAuth();
	}, [isAuthenticated, handoffSettled]);

	const value = useMemo(
		() => ({
			isAuthenticated,
			login,
			logout,
			isLoading,
		}),
		[isAuthenticated, isLoading, login, logout],
	);

	return (
		<AuthContext value={value}>
			{handoffSettled ? children : <Loader wrap color="accent" size="xl" />}
		</AuthContext>
	);
};
